import { FC } from "react";
import styled, { css } from "styled-components";
import { Line } from "react-chartjs-2";
import ReactTooltip from "react-tooltip";
import slugify from "slugify";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";

import Text from "@/components/Text";
import { numFormatter } from "@/utils/helpers";
import { TrendMetricCard } from "@/types/resources";
import useTrendGraph from "@/hooks/useTrendGraph";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

export const Card = styled.article`
  box-shadow: 0px 8px 26px rgba(34, 34, 34, 0.05);
  border-radius: 4px;
  padding: 16px;
  display: flex;
  position: relative;
  flex-wrap: wrap;

  ${({ theme }) => css`
    background: ${theme.colors.white};
  `}
`;

const InfoIcon = styled.svg`
  position: absolute;
  right: 16px;
  top: 14px;
  outline: none !important;
  cursor: help;
`;

const Graph = styled.div`
  width: 88px;
  margin-left: 15px;
  flex-shrink: 0;
  margin-top: auto;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Heading = styled(Text)`
  margin: 0 0 15px;
  width: 100%;
  padding-right: 40px;
`;

const Metric = styled.div`
  margin-top: auto;
`;

const Change = styled(Text)<{ $rotate: boolean }>`
  > svg {
    margin-right: 4px;
    ${({ $rotate }) =>
      $rotate &&
      css`
        transform: rotate(180deg);
      `}
  }
`;

const Total = styled(Text)`
  font-size: 30px;
`;

const TooltipTitle = styled(Text)`
  margin: 0 0 12px;
`;

const Tooltip = styled(ReactTooltip)`
  &.__react_component_tooltip {
    max-width: 280px;
    opacity: 1 !important;
    padding: 24px;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 4px;
    pointer-events: auto !important;
    box-shadow: 0px 8px 26px rgb(34 34 34 / 5%);
    &:hover {
      visibility: visible !important;
      opacity: 1 !important;
    }
  }
`;

const formatTotal = (num: number) => {
  if (num >= 100) {
    return numFormatter(Math.floor(num));
  }

  return numFormatter(num);
};

interface Props {
  card: TrendMetricCard;
}

const TrendCard: FC<Props> = ({ card }) => {
  const { data, labels } = card;

  const { graphData, color, graphOptions } = useTrendGraph(
    labels,
    data,
    card.percentage_change
  );

  return (
    <>
      <Card>
        {!!card.explanation && (
          <InfoIcon
            data-tip
            data-for={`tooltip-${slugify(card.title)}`}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="10" cy="10" r="9" stroke="#E9E9E9" strokeWidth="2" />
            <path
              d="M9 7.08824V5.5H11V7.08824H9ZM9 14.5V8.14706H11V14.5H9Z"
              fill="#909090"
            />
          </InfoIcon>
        )}
        <Heading $color="gray" $type="smallBold" as="h4">
          {card.title}
        </Heading>
        <Content>
          <Metric>
            <Total $type="heading1" as="h5">
              {card.prefix}
              {formatTotal(card.total)}
              {card.suffix}
            </Total>
            <Change
              $type="smallBold"
              as="span"
              $color={color}
              $rotate={card.percentage_change < 0}
            >
              <svg
                width="9"
                height="8"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.05693 0.258064C4.25385 -0.0860216 4.74615 -0.0860214 4.94307 0.258065L8.93069 7.22581C9.12761 7.56989 8.88146 8 8.48763 8H0.512375C0.118536 8 -0.127613 7.56989 0.0693064 7.22581L4.05693 0.258064Z"
                  fill="currentColor"
                />
              </svg>
              {card.percentage_change}%
            </Change>
          </Metric>
        </Content>
        <Graph>
          <Line data={graphData} options={graphOptions} />
        </Graph>
      </Card>
      {!!card.explanation && (
        <Tooltip
          id={`tooltip-${slugify(card.title)}`}
          effect="solid"
          place="bottom"
          delayHide={200}
          arrowColor="#fff"
          border
          borderColor="#e0e0e0"
        >
          <TooltipTitle $type="button">Explanation</TooltipTitle>
          <Text>{card.explanation}</Text>
        </Tooltip>
      )}
    </>
  );
};

export default TrendCard;
